import { Component, OnInit, OnDestroy, Renderer2 } from '@angular/core';
import { Router } from '@angular/router';
import { RoleService } from 'src/app/services/role.service';
import { SponsorsService } from 'src/app/services/sponsors.service';
import { CategoryService } from 'src/app/services/category.service';
import { forkJoin, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Component({
  selector: 'app-sponsers',
  templateUrl: './sponsers.component.html',
  styleUrls: ['./sponsers.component.scss']
})

export class SponsersComponent implements OnInit, OnDestroy {

  private destroy$ = new Subject<void>();
  availableClasses: string[] = ["body-dark", "bg-white", ""];
  currentClassIdx: number = 0;
  bodyClass: string;
  sponsorsData: any[] = [];
  categorylist: any[] = [];
  groupedSponsors: any[] = [];

  constructor(private renderer: Renderer2, private categoryService: CategoryService, private roleService: RoleService, private sponsorsService: SponsorsService, private router: Router) {
    this.bodyClass = this.availableClasses[this.currentClassIdx];
  }
  
  ngOnInit(): void {
    this.roleService.setCurrentActiveRoute2(this.router.url);
    this.loadSponsorsAndCategories();
    this.renderer.addClass(document.body, "sponserPage");
  }  
  
  loadSponsorsAndCategories() {
    const sponsorsQuery = '?filterByDisable=false';
    const categoriesQuery = '?filterByDisable=false&getSubcategory=true&pageSize=50&pageNumber=1&filterByParentCategory=Sponsors';  
    // Use forkJoin so both requests are available together
    forkJoin({
      sponsorsResp: this.sponsorsService.getSponsors(sponsorsQuery).pipe(
        catchError(err => {
          console.error('Sponsors API error', err);
          return of(null);
        })
      ),
      categoriesResp: this.categoryService.getCategoryWithoutAuthList(categoriesQuery).pipe(
        catchError(err => {
          console.error('Categories API error', err);
          return of(null);
        })
      )
    }).pipe(takeUntil(this.destroy$)).subscribe(({ sponsorsResp, categoriesResp }) => {      
      this.sponsorsData = this.extractSponsorsArray(sponsorsResp);
      this.categorylist = this.extractCategoriesArray(categoriesResp);     
      this.buildGroupedSponsors();
    });
  }
  
  extractSponsorsArray(resp: any): any[] {
    if (!resp) return [];
    // common patterns: resp.items.items OR resp.data.items OR resp.items
    if (Array.isArray(resp)) return resp;
    if (resp.items && Array.isArray(resp.items.items)) return resp.items.items;
    if (resp.data && Array.isArray(resp.data.items)) return resp.data.items;
    if (resp.items && Array.isArray(resp.items)) return resp.items;
    // fallback: whatever top-level array exists
    const arr = Object.values(resp).find(v => Array.isArray(v));
    return Array.isArray(arr) ? arr : [];
  }

  extractCategoriesArray(resp: any): any[] {
    if (!resp) return [];
    if (Array.isArray(resp)) return resp;
    if (resp.data && Array.isArray(resp.data.items)) return resp.data.items;
    if (resp.items && Array.isArray(resp.items)) return resp.items;
    const arr = Object.values(resp).find(v => Array.isArray(v));
    return Array.isArray(arr) ? arr : [];
  }

  // Get category id from sponsor — handle string or object
  getCategoryIdFromSponsor(sponsor: any): string | null {
    if (!sponsor) return null;
    // the category might be a string id or an object like { _id: '...', title: '...' }
    if (typeof sponsor.category === 'string' && sponsor.category.trim()) return sponsor.category;
    if (sponsor.category && typeof sponsor.category === 'object') {
      // common keys: _id, id
      return sponsor.category._id || sponsor.category.id || null;
    }
    // sometimes sponsor has categoryId field
    if (sponsor.categoryId) return sponsor.categoryId;
    return null;
  }

  hasSponsorLink(sponsor: any): boolean {
    const link = sponsor?.link;
    return typeof link === 'string' && link.trim().length > 0;
  }

  getSponsorLink(sponsor: any): string {
    const link = sponsor?.link;
    return typeof link === 'string' ? link.trim() : '';
  }
  
  buildGroupedSponsors() {
    // Build map: id -> meta
    const categoryMap: Record<string, { name: string; image?: string }> = {};
    this.categorylist.forEach(cat => {
      const id = cat._id || cat.id || cat._id?.toString();
      if (!id) return;
      categoryMap[id] = {
        name: cat.title || cat.name || 'Unnamed',
        image: cat.photo || cat.icon || cat.image || null
      };
    });
    // Keep order as returned by categorylist (IDs)
    const categoryOrder = this.categorylist.map(c => c._id || c.id).filter(Boolean);
    const groupedById: Record<string, any> = {}; // key = categoryId or 'others'
    this.sponsorsData.forEach(sponsor => {
      const catId = this.getCategoryIdFromSponsor(sponsor);
      const useId = catId || '__OTHERS__';
      if (!groupedById[useId]) {
        const meta = categoryMap[useId];
        groupedById[useId] = {
          categoryId: useId === '__OTHERS__' ? null : useId,
          categoryName: meta ? meta.name : (useId === '__OTHERS__' ? 'Others' : (sponsor?.category?.title || 'Unknown')),
          categoryImage: meta ? meta.image : null,
          sponsors: []
        };
      }
      groupedById[useId].sponsors.push(sponsor);
    });
    // Convert to array and sort by categoryOrder (with Others at the end)
    const groupedArray = Object.values(groupedById) as any[]; 
    groupedArray.sort((a, b) => {        
      if (!a.categoryId) return 1;
      if (!b.categoryId) return -1;      
      const iA = categoryOrder.indexOf(a.categoryId);
      const iB = categoryOrder.indexOf(b.categoryId);      
      return iB - iA;   //  DESC SORT
    });
    this.groupedSponsors = groupedArray;      
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
    this.renderer.removeClass(document.body, 'sponserPage');
  }

}
